/* -----------------------------------------------------------------------------
 * 發現與加入系統 (Device Discovery & Adoption) 邏輯說明
 * -----------------------------------------------------------------------------
 * 1. 資料獲取流程 (Initial Sequence)
 *    API 順序對資料加載至關重要：
 *    - [Org]:  站點列表 -> Inventory 帳號 -> 各站點帳號 -> 已存在設備
 *    - [Site]: Inventory 帳號 -> 各站點帳號 -> 已存在設備 -> Inventory 設備
 * 
 * 2. 選取規則 (Checkbox 勾選限制)
 *    - 驗證狀態一致性：只能同時勾選同狀態設備 (全為 passed 或 failed)。
 *    - 加入系統中鎖定：處於 provisioning 狀態的設備無法選取。
 *    - 網路設定限制：缺少 Gateway/DNS 之設備可勾選 (用於 IP 設定)，但禁止 Adopt。
 * 
 * 3. 加入系統與操作按鈕規則 (何時可以 Adopt?)
 *    設備必須同時滿足以下條件，才允許執行 Adopt：
 *    - 驗證通過：authStatus 為 passed (顯示為 Configurable)。
 *    - 網路有效：具備有效 Gateway 與 DNS (非 0.0.0.0)。
 *    - 非加入系統中：設備當前不處於 provisioning 狀態。
 * 
 *    按鈕具體行為：
 *    - 單一設備 (Slider): 滿足上述條件時 Adopt 按鈕才可點擊。
 *    - 批量操作 (Action Button):
 *      - Authenticate: 選中設備皆為 auth failed 時啟用。
 *      - IP Settings:  選中設備皆為 auth passed 時啟用。
 *      - Adopt:        選中設備皆為 auth passed 且「全部」滿足網路有效條件時啟用。
 * 
 * 4. 網路驗證標準
 *    - 有效 Gateway/DNS：非空值、非 null、且非 "0.0.0.0"。
 *    - 特殊限制：DXS-3610/3410 型號不支援特定 DNS 設定。
 * 
 * 5. 操作來源類型 (Adoption Sources)
 *    - byRow: 單一設備操作。
 *    - byCheckboxWithAuthenticate: 批量憑證驗證。
 *    - byCheckboxWithIpSettings:   批量網路資訊設定。
 *    - byCheckboxWithAdoption:     最終批量加入系統流程。
 * -------------------------------------------------------------------------- */

import mainStyle from "./mainStyle.module.scss";

import icon_stop from "assets/images/icons/icn_btn_stop.svg";
import icon_setting from "assets/images/icons/icn_btn_settings.svg";
import icon_setting_disabled from "assets/images/icons/icn_btn_settings_grey.svg";
import icon_start from "assets/images/icons/icn_btn_start.svg";
import icon_search_state from "assets/images/icons/icn_search.svg";
import icon_stopped_state from "assets/images/icons/cn_table_cell_stop_red.svg";
import icon_success_state from "assets/images/icons/icn_success.svg";

import { FormattedMessage } from "react-intl";
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { createColumnHelper } from '@tanstack/react-table';
import { v4 as uuidv4 } from "uuid";
import { cloneDeep, isEqual } from 'lodash';

// Const
import { deviceTypeOption } from "./const";


// Hook
import useAuthScope from "hooks/useAuthScope";
import useTableInstance from "hooks/useTableInstance";

// Component
import TableField from "components/Table";
import ModalContainer from 'components/ModalContainer';
import ModalSettings from "./modals/modalSettings";
import ModalDiscoveryError from "./modals/modalDiscoveryError";
import ModalError from "./modals/modalError";
import ModalAdoptionHint from './modals/modalAdoptionHint';
import ModalAdoption from './modals/ModalAdoption';
import Slider from './components/Slider';
import FloatingButton from "./components/FloatingButton";
import Loading from "./loading";

// Other
import discoverColumns from "./discoverColumns";
import {
  scanningTimeout, otherTimeout, socketIdTypeOption,
  scopeLevelOption, authStateOption, defaultLayerSetting, adoptionSourceType,
} from "./const";
import {
  getAPIKeyAndParams,
  makeDiscoveryPayload, makeStopDiscoverPayload, makeAdoptDevicePayload, makeIpSettingDevicesPayload,
  makeAuthenticateDevicesPayload, makeAdoptDevicesPayload
} from "./requests";
import {
  handleDiscoveryResponse, handleBatchIpSettingsResponse,
  handleBatchAuthenticationResponse, handleAdoptionResponse, handleUnexpectedResponse
} from "./response";

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;
const checkEnvironment = process.env.REACT_APP_RUNNING_CONTEXT;

const notSupportDNSDeviceModels = [
  'DXS-3610', 'DXS-3410'
];

const isNotSupportDNSDevice = (modelName) => {
  if (!modelName) {
    return false;
  }

  return notSupportDNSDeviceModels.some((prefix) => modelName.startsWith(prefix));
}

const AddDevice = ({
  sendRequest,
  dataMap,
  getData,
  changeDiscoverBatchActionsStatus,
  discoverBatchActionTarget,
  setDiscoverBatchActionTarget,
  searchVal, setIsEmptyTable,
  fetchTableData,
  goToThePreviousTab,
  existedProxyDevices = [],
}) => {
  const navigate = useNavigate();
  const { auth, isAuthReady } = useAuthScope();
  const isCloudVersion = checkEnvironment === 'cloud' ? true : false;

  // State
  const scanningAbortRef = useRef(false);
  const [isInitialized, setIsInitialized] = useState({
    sites: false,
    inventoryAccount: false,
    deviceAccounts: false,
    existedDevices: false,
    inventoryDevices: false,
  });
  const [sitesSocketId, setSitesSocketId] = useState(null);
  const [sites, setSites] = useState([]);
  const [existedDevicesSocketId, setExistedDevicesSocketId] = useState(null);
  const [existedDevices, setExistedDevices] = useState([]);
  const [proxyDevices, setProxyDevices] = useState(cloneDeep(existedProxyDevices));
  const [selectedProxyDevice, setSelectedProxyDevice] = useState(null);
  const [inventorySocketId, setInventorySocketId] = useState(null);
  const [inventoryDevices, setInventoryDevices] = useState([]);
  const [layerSetting, setLayerSetting] = useState(cloneDeep(defaultLayerSetting));
  const [inventoryDeviceAccountSocketId, setInventoryDeviceAccountSocketId] = useState(null);
  const [inventoryDeviceAccount, setInventoryDeviceAccount] = useState(null);
  const [deviceAccountSocketId, setDeviceAccountSocketId] = useState(null);
  const [deviceAccounts, setDeviceAccounts] = useState([]);
  const [scanAuthSocketIds, setScanAuthSocketIds] = useState([]);
  const [socketId, setSocketId] = useState(null);
  const [stopStopId, setStopStopId] = useState(null);
  const [socketIdType, setSocketIdType] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [scanningStopped, setScanningStopped] = useState(false);
  const [scanningDone, setScanningDone] = useState(false);
  const [originDevices, setOriginDevices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [renderCount, setRenderCount] = useState(0);
  const [showModalSettings, setShowModalSettings] = useState((isCloudVersion && renderCount) === 0 ? true : false);
  const [showModalDiscoveryError, setShowModalDiscoveryError] = useState(false);
  const [showModalNoSiteError, setShowModalNoSiteError] = useState(false);
  const [showModalAdoptionHint, setShowModalAdoptionHint] = useState(false);
  const [showModalAdoption, setShowModalAdoption] = useState(false);
  const [openSlider, setOpenSlider] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [adoptionSource, setAdoptionSource] = useState(null);
  const [stopAdoption, setStopAdoption] = useState(false);
  const [readyDevices, setReadyDevices] = useState([]);
  const [results, setResults] = useState([]);
  const [tempInfoFromAdopt, setTempInfoFromAdopt] = useState({ siteId: "", username: "", password: "", });
  const [forTestOnly, setForTestOnly] = useState(1);
  const [showConfirmServiceChangeModal, setShowConfirmServiceChangeModal] = useState(false);
  const [preConfirmSocketId, setPreConfirmSocketId] = useState(null);
  const [preConfirmResult, setPreConfirmResult] = useState(null);
  const [isPreConfirmLoading, setIsPreConfirmLoading] = useState(false);

  const isOrgLevel = auth?.scopeLevel === scopeLevelOption.org;
  const isSiteLevel = auth?.scopeLevel === scopeLevelOption.site;

  // Method
  const gotoCreateSite = () => {
    navigate("/org/sites");
  }

  const handleSelectProxyDevice = (device) => {
    setSelectedProxyDevice(device);
  }

  const selectProxyDeviceCallback = () => {
    setShowModalSettings(false);
    setRenderCount(renderCount + 1);
  }

  const notSelectProxyDeviceCallback = () => {
    goToThePreviousTab();
  }

  const fetchExistedDevices = () => {
    // Fetch existed devices(Either org level or site level)
    const newExistedDevicesSocketId = uuidv4();
    setExistedDevicesSocketId(newExistedDevicesSocketId);

    if (isOrgLevel) {
      const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'existedDevices');

      sendRequest(
        apiKey,
        newExistedDevicesSocketId,
        placeholder,
        { filter: { type: 'all' } }
      );
    }

    if (isSiteLevel) {
      const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'existedDevices', sites[0].id);

      sendRequest(
        apiKey,
        newExistedDevicesSocketId,
        placeholder,
        { filter: { type: 'all' } }
      );
    }
  }

  const cleanDevices = () => {
    setOriginDevices([]);
    setDevices([]);
  }

  const scanDevices = async () => {
    setScanAuthSocketIds([]);
    const payload = makeDiscoveryPayload({
      auth,
      layerSetting,
      selectedProxyDevice,
    });

    setSocketId(payload.socketId);
    sendRequest(payload.apiKey, payload.socketId, payload.placeholder, payload.params);
    setSocketIdType(socketIdTypeOption.discovery);
    setStopStopId(null);
  }

  const handleAutoAuth = (devices) => {
    setOriginDevices((prevDevices) => {
      const newDevices = [...devices, ...prevDevices];
      return newDevices;
    });

    setDevices((prevDevices) => {
      const newDevices = [...devices, ...prevDevices];
      return newDevices;
    });

    // Important! this must run after the devices have been collected.
    if (devices.length !== 0) {
      const newSocketId = uuidv4();
      const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'authenticate');
      const param = {
        timeout: otherTimeout,
        data: [],
      };

      devices.forEach((updatedDevice) => {
        const data = {
          layer: updatedDevice.layer,
          ddpVersion: updatedDevice.ddpVersion,
          destMacAddress: updatedDevice.macAddress,
          destIpAddress: updatedDevice.ipAddress,
          username: updatedDevice.username,
          password: updatedDevice.password,
        };

        if (updatedDevice.layer === 3) {
          data.ipVersion = updatedDevice.ipVersion;
        }

        param.data.push(data);
      });

      sendRequest(
        apiKey,
        newSocketId,
        placeholder,
        param,
      );

      setScanAuthSocketIds((prevSocketIds) => [...prevSocketIds, newSocketId]);
    }
  }

  const stopScanningDevices = () => {
    setOpenSlider(false);
    scanningAbortRef.current = true;
    setScanningStopped(true);
    setScanningDone(true);

    const payload = makeStopDiscoverPayload({ auth });
    setSocketId(payload.socketId);
    sendRequest(payload.apiKey, payload.socketId, payload.placeholder, payload.params);
  }

  const searchDevices = (value) => {
    if (value === "") {
      setDevices(cloneDeep(originDevices));
      return;
    }

    const filteredDevices = originDevices.filter((device) => {
      if (device.macAddress.includes(value)) {
        return true;
      }

      if (device.modelName.includes(value)) {
        return true;
      }

      if (device.ipAddress.includes(value)) {
        return true;
      }

      return false;
    });

    setDevices(filteredDevices);
  }

  const rediscover = () => {
    setOpenSlider(false);
    setStopAdoption(true);
    scanningAbortRef.current = false;
    setScanningStopped(false);
    setScanningDone(false);

    cleanDevices();
    scanDevices();
  }

  const retryScanningDevices = async () => {
    setOriginDevices([]);
    setDevices([]);
    setScanningDone(false);
    setShowModalDiscoveryError(false);
    setOpenSlider(false);
    setSelectedDevice(null);

    // Re-scan devices
    scanDevices();
  }

  const toggleAllDeviceSelection = (flag) => {
    const firstCheckDevice = devices.find((device) => device.checked === true);
    const updatedDevices = devices.map((device) => {
      if (device.authStatus !== firstCheckDevice.authStatus) {
        return device;
      }

      return {
        ...device,
        checked: flag,
      };
    });

    setDevices(updatedDevices);
  }

  const toggleDeviceSelection = (macAddress) => {
    const findDevice = devices.find((device) => device.macAddress === macAddress);
    if (!findDevice || !findDevice.authStatus) {
      return;
    }

    const updatedDevices = devices.map((device) => {
      if (device.macAddress === macAddress) {
        const checked = !device.checked;
        return {
          ...device,
          checked,
        };
      }
      return device;
    },);

    setDevices(updatedDevices);

    // Get the first selected device's status
    const selectedDevices = updatedDevices.filter((device) => device.checked);
    if (selectedDevices.length > 0) {
      setSelectedStatus(selectedDevices[0]);
    } else {
      setSelectedStatus(null);
    }

    // Updated selected devices
    const selectedDevicesList = updatedDevices.filter((device) => device.checked);
    setSelectedDevices(selectedDevicesList);
  }

  const disableCheckbox = (device) => {
    if (
      (selectedStatus && !isEqual(device.authStatus, selectedStatus.authStatus))
      || device.provisioning
    ) {
      return true;
    }

    return false;
  }

  const checkBatchAdoptButtonStatus = () => {
    if (selectedDevices.length === 0) {
      return null;
    }

    const hasFailedAuth = selectedDevices.some((device) => device.authStatus === authStateOption.failed);

    return {
      hasFailedAuth,
    };
  }

  const startAdoptProcessForOneDevice = () => {
    setOpenSlider(false);

    if (sites.length === 0) {
      setShowModalNoSiteError(true);
      return;
    }

    setAdoptionSource(adoptionSourceType.byRow);
    setShowModalAdoption(true);
    setReadyDevices([]);
    setResults([]);
    setStopAdoption(false);
  }

  const changeToConfigurable = (targetDevice, username, password) => {
    const updatedDevices = devices.map((dev) => {
      if (dev.macAddress === targetDevice.macAddress) {
        return {
          ...dev,
          authStatus: authStateOption.passed,
          username,
          password,
        };
      }

      return dev;
    });

    setSelectedDevice({
      ...targetDevice,
      authStatus: authStateOption.passed,
      username,
      password,
    });
    setDevices(updatedDevices);
    setOriginDevices(updatedDevices);
  }

  const changeToConfigurables = (targetDevices, username, password) => {
    const updatedDevices = devices.map((dev) => {
      const targetDevice = targetDevices.find((target) => target.macAddress === dev.macAddress);
      if (targetDevice) {
        return {
          ...dev,
          // status: deviceStatusOption.configurable,
          authStatus: authStateOption.passed,
          username,
          password,
        };
      }
      return dev;
    });

    const updatedReadyDevices = readyDevices.map((dev) => {
      const targetDevice = targetDevices.find((target) => target.macAddress === dev.macAddress);
      if (targetDevice) {
        return {
          ...dev,
          // status: deviceStatusOption.configurable,
          authStatus: authStateOption.passed,
          username,
          password,
        };
      }
      return dev;
    });

    setReadyDevices(updatedReadyDevices);
    setDevices(updatedDevices);
    setOriginDevices(updatedDevices);
  }

  const handleBatch = (whichAdoptionSource) => {
    const status = checkBatchAdoptButtonStatus();
    if (!status) {
      return;
    }

    if (whichAdoptionSource === adoptionSourceType.byCheckboxWithIpSettings) {
      if (status.hasFailedAuth) {
        return;
      }
    }

    if (whichAdoptionSource === adoptionSourceType.byCheckboxWithAdoption) {
      if (status.hasFailedAuth) {
        return;
      }
    }

    setOpenSlider(false);
    if (sites.length === 0) {
      setShowModalNoSiteError(true);
      return;
    }

    setDevices((prevDevices) => {
      const updatedDevices = prevDevices.map((device) => {
        return {
          ...device,
          prevChecked: false,
        };
      });
      return updatedDevices;
    });

    setAdoptionSource(whichAdoptionSource);
    setShowModalAdoption(true);
    setReadyDevices([]);
    setResults([]);
    setStopAdoption(false);
  }

  const sendAdoptionRequest = async (
    adoptionSource, selectedDevices, selectedDevice,
    selectedSiteId, username, password,
    selectedIpMode, ipAddress, subnetMask, gateway, dns1, dns2,
    sshUsername, sshPassword,
  ) => {
    // org-level site id could be
    // 1. None -> Inventory
    // 2. Non-none -> Selected site
    // Site-level site id is always the current site id
    if (auth.scopeLevel === scopeLevelOption.site) {
      selectedSiteId = auth.sites.id;
    }

    setResults([]);
    setTempInfoFromAdopt({
      siteId: selectedSiteId,
      username,
      password,
    });

    if (adoptionSource === adoptionSourceType.byRow) {
      const payload = makeAdoptDevicePayload({
        auth,
        selectedSiteId,
        selectedDevice,
        sshUsername,
        sshPassword,
      });

      sendRequest(payload.apiKey, payload.socketId, payload.placeholder, payload.params);
      setSocketId(payload.socketId);
      setSocketIdType(socketIdTypeOption.adopt);
    }

    if (adoptionSource === adoptionSourceType.byCheckboxWithIpSettings) {
      const payload = makeIpSettingDevicesPayload({
        auth,
        selectedDevices,
        selectedIpMode,
        config: { ipAddress, subnetMask, gateway, dns1, dns2 },
        isNotSupportDNSDevice,
      });

      sendRequest(payload.apiKey, payload.socketId, payload.placeholder, payload.params);
      setSocketId(payload.socketId);
      setSocketIdType(socketIdTypeOption.ipSetting);
    }

    if (adoptionSource === adoptionSourceType.byCheckboxWithAuthenticate) {
      setTempInfoFromAdopt({
        siteId: selectedSiteId,
        username,
        password,
      });

      const payload = makeAuthenticateDevicesPayload({
        auth,
        selectedDevices,
        username,
        password,
      });
      sendRequest(payload.apiKey, payload.socketId, payload.placeholder, payload.params);
      setSocketId(payload.socketId);
      setSocketIdType(socketIdTypeOption.auth);
    }

    if (adoptionSource === adoptionSourceType.byCheckboxWithAdoption) {
      const payload = makeAdoptDevicesPayload({
        auth,
        selectedSiteId,
        selectedDevices,
        sshUsername,
        sshPassword
      });

      sendRequest(payload.apiKey, payload.socketId, payload.placeholder, payload.params);
      setSocketId(payload.socketId);
      setSocketIdType(socketIdTypeOption.adopt);
    }
  }

  const sendPreConfirmRequest = (selectedSiteId, devicesToAdopt) => {
    const isUnityRd3 = window.location.hostname.includes('unity-rd3');
    if (!isUnityRd3) return;

    setIsPreConfirmLoading(true);
    setPreConfirmResult(null);
    const newSocketId = uuidv4();
    setPreConfirmSocketId(newSocketId);

    const finalSiteId = auth.scopeLevel === scopeLevelOption.site ? auth.sites.id : selectedSiteId;

    const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'preConfirm');
    const params = [
      {
        siteId: finalSiteId,
        action: "add",
        devices: devicesToAdopt.map(d => ({
          modelName: d.modelName,
          deviceId: d.macAddress,
        }))
      }
    ];

    sendRequest(apiKey, newSocketId, placeholder, params);
  };

  const updatedDeviceAfterAdoption = (result) => {
    if (stopAdoption) {
      return;
    }

    setDevices((prevDevices) => {
      const updatedDevices = prevDevices.map((device) => {
        if (device.macAddress === result.macAddress) {
          device.prevChecked = true;
          if (result.isAuthenticated && result.isAdopted) {
            device.provisioning = true;
            device.checked = false;
          } else {
            device.provisioning = false;
          }
        }

        return device;
      });

      return updatedDevices;
    });
  }

  const updatedDeviceAfterAdoptions = (results) => {
    if (stopAdoption) {
      return;
    }

    setDevices((prevDevices) => {
      const updatedDevices = prevDevices.map((device) => {
        const result = results.find((res) => res.macAddress === device.macAddress);
        if (result) {
          device.prevChecked = true;

          if (result.isAuthenticated && result.isAdopted) {
            device.provisioning = true;
          } else {
            device.provisioning = false;
          }
        }

        return device;
      });

      return updatedDevices;
    });
  };

  const closeAdoption = () => {
    setShowConfirmServiceChangeModal(false);
    setAdoptionSource(null);
    setShowModalAdoption(false);
    setSelectedDevice(null);

    const successDevices = results.filter((result) => result.isAdopted).map((result) => result.macAddress);
    const newDevices = [];
    const newSelectedDevices = [];

    for (let i = 0; i < devices.length; i++) {
      const device = cloneDeep(devices[i]);
      if (successDevices.includes(device.macAddress)) {
        continue;
      }

      device.prevChecked = false;
      newDevices.push(device);
    }

    for (let i = 0; i < selectedDevices.length; i++) {
      const device = cloneDeep(selectedDevices[i]);
      if (successDevices.includes(device.macAddress)) {
        continue;
      }

      device.prevChecked = false;
      newSelectedDevices.push(device);
    }

    setDevices(newDevices);
    setSelectedDevices(newSelectedDevices);
    fetchTableData();
  }

  const toggleSlider = () => {
    setOpenSlider(false);
    setSelectedDevice(null);
  }

  const columnHelper = createColumnHelper()
  const columnsData = discoverColumns(
    columnHelper, disableCheckbox, toggleDeviceSelection,
    scanningDone, devices, toggleAllDeviceSelection
  );
  const table = useTableInstance({
    tableName: "provisionTable",
    data: devices,
    columns: columnsData,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  // Side effect
  useEffect(() => {
    setIsEmptyTable(devices.length === 0);
  });

  /**
   **************************************************
   *  Handle initial fetching                       *
   **************************************************
   *  The sequence of the API calls is important.
   *
   *  [Org Level]
   *  1. Sites
   *  2. Inventory device account
   *  3. Device accounts
   *  4. Existed devices
   * 
   *  [Site Level]
   *  1. Inventory device account
   *  2. Device accounts
   *  3. Existed devices
   *  4. Inventory devices
   * 
   *  After all data are fetched, set the isInitialized state to true
   *  If environment is cloud, after initial data fetched, fetching proxies data.
   *
   */
  useEffect(() => {
    if (isAuthReady) {
      if (isOrgLevel) {
        if (!sitesSocketId) {
          const newSitesSocketId = uuidv4();
          setSitesSocketId(newSitesSocketId);

          // Fetch sites(org level)
          const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'sites');
          sendRequest(
            apiKey,
            newSitesSocketId,
            placeholder,
            null,
          );
        }
      } else {
        setIsInitialized((prevState) => ({
          ...prevState,
          sites: true,
        }));

        // Fetch inventory device account(Site level)
        if (!inventoryDeviceAccount) {
          setSites([{ id: auth.sites.id, name: auth.sites.name, fetchedAccount: false }]);
          const newInventoryDeviceAccountSocketId = uuidv4();
          setInventoryDeviceAccountSocketId(newInventoryDeviceAccountSocketId);
          const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'inventoryDeviceAccount');

          sendRequest(
            apiKey,
            newInventoryDeviceAccountSocketId,
            placeholder,
            null
          );
        }
      }
    }
  }, [isAuthReady])

  useEffect(() => {
    if (sites.length === 0) {
      return;
    }

    if (sites.length === deviceAccounts.length) {
      setIsInitialized((prevState) => ({
        ...prevState,
        deviceAccounts: true,
      }));

      fetchExistedDevices();
    }
  }, [sites, deviceAccounts]);

  useEffect(() => {
    searchDevices(searchVal);
  }, [searchVal]);

  useEffect(() => {
    if (selectedDevices.length === 0) {
      return changeDiscoverBatchActionsStatus(null);
    }

    const status = checkBatchAdoptButtonStatus();
    changeDiscoverBatchActionsStatus(status);
  }, [selectedDevices]);

  useEffect(() => {
    if (!discoverBatchActionTarget) {
      return;
    }

    const status = checkBatchAdoptButtonStatus();

    if (discoverBatchActionTarget === socketIdTypeOption.ipSetting) {
      (status && !status.hasFailedAuth) && handleBatch(adoptionSourceType.byCheckboxWithIpSettings);
      setDiscoverBatchActionTarget(null);
    }

    if (discoverBatchActionTarget === socketIdTypeOption.auth) {
      (status && status.hasFailedAuth) && handleBatch(adoptionSourceType.byCheckboxWithAuthenticate)
      setDiscoverBatchActionTarget(null);
    }

    if (discoverBatchActionTarget === socketIdTypeOption.adopt) {
      (status && !status.hasFailedAuth) && handleBatch(adoptionSourceType.byCheckboxWithAdoption);
      setDiscoverBatchActionTarget(null);
    }
    setSelectedDevice(null);
    setOpenSlider(false);
  }, [discoverBatchActionTarget]);

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    if (
      isInitialized.sites
      && isInitialized.inventoryAccount
      && isInitialized.deviceAccounts
      && isInitialized.existedDevices
      && isInitialized.inventoryDevices) {
      return;
    }

    const hasData = [
      sitesSocketId, inventoryDeviceAccountSocketId, deviceAccountSocketId,
      existedDevicesSocketId, inventorySocketId
    ].some(id => dataMap[id]);

    if (!hasData) return;

    // Sites
    const sitesResponse = getData(sitesSocketId);
    if (sitesResponse) {
      const updatedSites = sitesResponse.data.sites.map((site) => ({
        id: site.siteId,
        name: site.siteName,
        fetchedAccount: false,
      }));

      setSites(updatedSites);
      setIsInitialized((prevState) => ({
        ...prevState,
        sites: true,
      }));

      // Fetch inventory device account(Org level)
      if (!inventoryDeviceAccount) {
        const newInventoryDeviceAccountSocketId = uuidv4();
        setInventoryDeviceAccountSocketId(newInventoryDeviceAccountSocketId);
        const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'inventoryDeviceAccount');

        sendRequest(
          apiKey,
          newInventoryDeviceAccountSocketId,
          placeholder,
          null
        );
      }
    }

    // Inventory Device Account
    const inventoryAccountResponse = getData(inventoryDeviceAccountSocketId);
    if (inventoryAccountResponse) {
      setInventoryDeviceAccount(inventoryAccountResponse.data || null);
      setIsInitialized((prevState) => ({
        ...prevState,
        inventoryAccount: true,
      }));

      // Fetch device accounts(fetch the first device account no matter org level or site level)
      if (sites.length > 0) {
        const site = sites[0];
        const newSocketId = uuidv4();
        setDeviceAccountSocketId(newSocketId);
        const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'deviceAccount', site.id);

        sendRequest(
          apiKey,
          newSocketId,
          placeholder,
          null,
        );

        setSites((prevSites) => {
          const newSites = prevSites.map((s) => {
            if (s.id === site.id) {
              return {
                ...s,
                fetchedAccount: true,
              };
            }
            return s;
          });
          return newSites;
        });
      } else {
        setIsInitialized((prevState) => ({
          ...prevState,
          deviceAccounts: true,
        }));
        fetchExistedDevices();
      }
    }

    // Device Accounts(fetch the resting device accounts no matter org level or site level)
    const deviceAccountResponse = getData(deviceAccountSocketId);
    if (deviceAccountResponse) {
      const response = deviceAccountResponse;
      const siteId = response.resource.split("/sites/")[1].split("/")[0];
      const updatedDeviceAccount = cloneDeep(deviceAccounts);
      updatedDeviceAccount.push({
        id: siteId,
        username: response.data.username,
        password: response.data.password,
      });
      setDeviceAccounts(updatedDeviceAccount);

      const restSites = sites.filter((site) => site.fetchedAccount === false);
      if (restSites.length > 0) {
        const site = restSites[0];
        const newSocketId = uuidv4();
        setDeviceAccountSocketId(newSocketId);
        const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'deviceAccount', site.id);

        sendRequest(
          apiKey,
          newSocketId,
          placeholder,
          null,
        );

        setSites((prevSites) => {
          const newSites = prevSites.map((s) => {
            if (s.id === site.id) {
              return {
                ...s,
                fetchedAccount: true,
              };
            }
            return s;
          });
          return newSites;
        });
      }
    }

    // Existed Devices
    const existedDevicesResponse = getData(existedDevicesSocketId);
    if (existedDevicesResponse) {
      const updatedExistedDevices = [];
      const updateInventoryDevices = [];

      existedDevicesResponse.data.forEach((device) => {
        const info = device.info;

        if (info.deviceType === deviceTypeOption.proxy.value) {
          return;
        }

        if (info.siteName) {
          updatedExistedDevices.push({
            productCategory: info.deviceType,
            macAddress: info.macAddress,
            registrationState: info.registrationState,
            linkStatus: info.linkStatus,
            siteId: info.siteId,
          });
        }

        if (isOrgLevel && !info.siteName) {
          updateInventoryDevices.push({
            productCategory: info.deviceType,
            modelName: info.modelName,
            macAddress: info.macAddress,
            deviceIPAddress: info.localIpAddr,
            fwVersion: info.firmwareVersion,
            hardwareVersion: info.hardwareVersion,
            protocolType: info.managedProtocol,
            registrationState: info.registrationState,
            linkStatus: info.linkStatus,
          });
        }
      });

      setExistedDevices(updatedExistedDevices);
      if (isOrgLevel) {
        setInventoryDevices(updateInventoryDevices);
      }

      setIsInitialized((prevState) => ({
        ...prevState,
        existedDevices: true
      }));

      // Fetch inventory devices(site level)
      if (!inventorySocketId) {
        const newInventorySocketId = uuidv4();
        setInventorySocketId(newInventorySocketId);

        if (isOrgLevel) {
          setIsInitialized((prevState) => ({
            ...prevState,
            inventoryDevices: true,
          }));
        }

        if (isSiteLevel) {
          const { apiKey, placeholder } = getAPIKeyAndParams(auth, 'inventoryDevices', sites[0].id);

          sendRequest(
            apiKey,
            newInventorySocketId,
            placeholder,
            null
          );
        }
      }
    }

    //  Inventory Devices
    const inventoryDevicesResponse = getData(inventorySocketId);
    if (inventoryDevicesResponse) {
      const updatedInventoryDevices = [];
      inventoryDevicesResponse.data.forEach((device) => {
        const info = device.info;
        updatedInventoryDevices.push({
          productCategory: info.deviceType,
          modelName: info.modelName,
          macAddress: info.macAddress,
          deviceIPAddress: info.localIpAddr,
          fwVersion: info.firmwareVersion,
          hardwareVersion: info.hardwareVersion,
          protocolType: info.managedProtocol,
          registrationState: info.registrationState,
          linkStatus: info.linkStatus,
        });
      });

      setInventoryDevices(updatedInventoryDevices);
      setIsInitialized((prevState) => ({
        ...prevState,
        inventoryDevices: true,
      }));
    }
  }, [dataMap]);

  useEffect(() => {
    if (isInitialized.sites
      && isInitialized.inventoryAccount
      && isInitialized.deviceAccounts
      && isInitialized.existedDevices
      && isInitialized.inventoryDevices) {
      if (isCloudVersion) {
        // Fetch proxies data here if needed in cloud version
      }
    }
  }, [isInitialized]);

  useEffect(() => {
    if (preConfirmSocketId && dataMap[preConfirmSocketId]) {
      const response = getData(preConfirmSocketId);
      if (response) {
        setPreConfirmResult(response);
        setIsPreConfirmLoading(false);
      }
    }
  }, [dataMap, preConfirmSocketId]);

  /**
   * Handle discovery, IP setting, authentication, and adoption responses
   */
  useEffect(() => {
    if (!socketId || !dataMap[socketId]) {
      return;
    }

    const response = getData(socketId);
    if (!response) {
      return;
    }

    const { resource, data } = response;
    if (!resource) {
      return;
    }

    // Discovery response
    if (!stopStopId && resource.includes("discovery")) {
      // Avoid error when the data is empty.
      if (data && data.length === 0) {
        setScanningDone(true);
        return;
      }

      const updatedDevices = handleDiscoveryResponse({
        inventoryDeviceAccount,
        deviceAccounts,
        existedDevices,
        inventoryDevices,
        data,
        devices
      });

      handleAutoAuth(updatedDevices);
    }

    // IP setting response
    if (resource.includes("ipSetting")) {
      // Avoid error when the data is empty.
      if (!data || data.length === 0) {
        return;
      }

      const updatedResults = cloneDeep(results);
      const updatedReadyDevices = cloneDeep(readyDevices);

      if (!data) {
        for (let i = 0; i < updatedReadyDevices.length; i++) {
          const device = updatedReadyDevices[i];
          updatedResults.push({
            macAddress: device.macAddress,
            isIpSetting: false,
          });
        }

        setResults(updatedResults);
        setReadyDevices(updatedReadyDevices);
        return;
      }

      if (checkMockup) {
        const cloneData = cloneDeep(data);
        const updatedData = [];

        for (let i = 0; i < cloneData.length; i++) {
          const selectedDevice = updatedReadyDevices.find((device) => device.macAddress === cloneData[i].macAddress);
          if (selectedDevice) {
            updatedData.push({
              ...cloneData[i],
              code: 0, // Mockup always pass
              message: "Success",
            });
          }
        }

        handleBatchIpSettingsResponse({ data: updatedData, results: updatedResults, readyDevices: updatedReadyDevices });
      } else {
        const updatedData = [];
        for (let i = 0; i < data.length; i++) {
          const selectedDevice = readyDevices.find((device) => device.macAddress === data[i].macAddress);
          if (selectedDevice) {
            updatedData.push(data[i]);
          }
        }

        handleBatchIpSettingsResponse({ data: updatedData, results: updatedResults, readyDevices: updatedReadyDevices });
      }

      setResults(updatedResults);
    }

    // Authentication response
    if (resource.includes("authenticate")) {
      // Avoid error when the data is empty.
      if (!data || data.length === 0) {
        return;
      }

      const updatedResults = cloneDeep(results);
      const updatedReadyDevices = cloneDeep(readyDevices);

      if (checkMockup) {
        const cloneData = cloneDeep(data);
        cloneData.map((device) => {
          device.code = 0; // Mockup always pass
          device.message = "Success";
          return device;
        });

        handleBatchAuthenticationResponse({ data: cloneData, results: updatedResults, readyDevices: updatedReadyDevices });
      } else {
        handleBatchAuthenticationResponse({ data, results: updatedResults, readyDevices: updatedReadyDevices });
      }

      setResults(updatedResults);
      setReadyDevices(updatedReadyDevices);
    }

    if (resource.includes("adopt")) {
      // Avoid error when the data is empty.
      if (!data || data.length === 0) {
        return;
      }

      const updatedData = [];

      // Merge adoptedResult into data
      const hasAdoptedReadyDevices = readyDevices.filter((device) => {
        return device.adoptedResult;
      });

      hasAdoptedReadyDevices.forEach((device) => {
        updatedData.push({
          ...device.adoptedResult,
        });
      });

      if (checkMockup) {
        const cloneData = [];
        let startIndex = 0;
        let endIndex = 0;

        if (forTestOnly === 1) {
          startIndex = 0;
          endIndex = 1;
        }

        if (forTestOnly === 2) {
          startIndex = 1;
          endIndex = 3;
        }

        if (forTestOnly === 3) {
          startIndex = 3;
          endIndex = data.length;
        }

        for (let i = startIndex; i < endIndex; i++) {
          cloneData.push({
            ...data[i],
          });
        }

        for (let i = 0; i < cloneData.length; i++) {
          const selectedDevice = readyDevices.find((device) => device.macAddress === cloneData[i].macAddress);
          if (selectedDevice) {
            updatedData.push({
              ...cloneData[i],
              code: cloneData[i].code,
              message: cloneData[i].message,
            });
          }
        }

        setForTestOnly(forTestOnly + 1);
        if (forTestOnly !== 3) {
          setTimeout(() => {
            // Send again after 10 seconds to simulate second batch
            const payload = makeAdoptDevicesPayload({
              auth,
              selectedSiteId: 'Taipei',
              selectedDevices,
              sshUsername: 'admin',
              sshPassword: 'admin'
            });

            sendRequest(payload.apiKey, payload.socketId, payload.placeholder, payload.params);
            setSocketId(payload.socketId);
            setSocketIdType(socketIdTypeOption.adopt);
          }, 3000);
        }
      } else {
        for (let i = 0; i < data.length; i++) {
          updatedData.push(data[i]);
        }
      }

      const { updatedResults, updatedReadyDevices } = handleAdoptionResponse({ data: updatedData, readyDevices });
      setResults(updatedResults);
      setReadyDevices(updatedReadyDevices);
    }
  }, [dataMap, socketIdType, socketId, results, readyDevices]);

  /**
   * Handle the scanning authentication response
   * - Update the devices' authStatus based on the response data
   * - If the response contains authentication data, update the devices' authStatus accordingly
   */
  useEffect(() => {
    if (!scanAuthSocketIds.some(id => dataMap[id])) {
      return;
    }

    scanAuthSocketIds.forEach((socketId) => {
      const response = getData(socketId);
      if (!response) {
        return;
      }

      const resource = response.resource;
      const data = response.data;

      if (resource.includes("authenticate")) {
        if (!data || data.length === 0) {
          return;
        }

        setOriginDevices((prevDevices) => {
          const newDevices = prevDevices.map((device) => {
            const authDevice = data.find((d) => d.macAddress === device.macAddress);
            if (authDevice) {
              return {
                ...device,
                authStatus: authDevice.code === 0 ? authStateOption.passed : authStateOption.failed
              };
            }
            return device;
          });

          return newDevices;
        });

        setDevices((prevDevices) => {
          const newDevices = prevDevices.map((device) => {
            const authDevice = data.find((d) => d.macAddress === device.macAddress);
            if (authDevice) {
              return {
                ...device,
                authStatus: authDevice.code === 0 ? authStateOption.passed : authStateOption.failed
              };
            }

            return device;
          });

          return newDevices;
        });
      }
    });
  }, [dataMap, scanAuthSocketIds]);

  useEffect(() => {
    let timer = null;
    let seconds = 0;

    if (checkMockup) {
      seconds = otherTimeout * 1000;
    } else {
      seconds = (
        (socketIdType === socketIdTypeOption.discovery
          || socketIdType === socketIdTypeOption.adopt) ? scanningTimeout : otherTimeout) * 1000;
    }

    if (socketId) {
      timer = setTimeout(() => {
        const updatedResults = handleUnexpectedResponse({ socketIdType, results, readyDevices });
        setResults(updatedResults);
        setScanningDone(true);
      }, seconds);

      return () => clearTimeout(timer);
    }
  }, [readyDevices, results, socketId, socketIdType]);

  useEffect(() => {
    // Find checked devices
    const updatedSelectedDevices = devices.filter((device) => device.checked);
    setSelectedDevices(updatedSelectedDevices);
    setSelectedStatus(updatedSelectedDevices.length > 0 ? updatedSelectedDevices[0] : null);
  }, [devices]);

  // Workaround
  // - Every time results are updated, invoke updatedDeviceAfterAdoption
  // - Performance bad if results are large, because it will update all devices again and again
  useEffect(() => {
    if (adoptionSource !== adoptionSourceType.byRow) {
      if (results.length === selectedDevices.length) {
        // Adoption completed
        if (results.length > 0 && results[0].hasOwnProperty('isAdopted')) {
          updatedDeviceAfterAdoptions(results);
          return;
        }

        // Authentication completed
        const authenticatedSuccessDevices = [];
        for (let i = 0; i < results.length; i++) {
          if (results[i].isAuthenticated) {
            authenticatedSuccessDevices.push(devices.find((device) => device.macAddress === results[i].macAddress));
          }
        }

        if (authenticatedSuccessDevices.length > 0) {
          changeToConfigurables(authenticatedSuccessDevices, tempInfoFromAdopt.username, tempInfoFromAdopt.password);
        }
      }
    } else {
      if (results.length === 1) {
        if (results[0].hasOwnProperty('isAdopted')) {
          updatedDeviceAfterAdoption(results[0]);
        }
      }
    }
  }, [results]);

  useEffect(() => {
    if (!showModalAdoption) {
      // Uncheck authenticated devices
      const updatedDevices = devices.map((device) => {
        if (device.checked && device.authStatus === authStateOption.passed) {
          return {
            ...device,
            checked: false,
          };
        }
        return device;
      });

      setDevices(updatedDevices);
      setOriginDevices(updatedDevices);
    }
  }, [showModalAdoption]);

  // Check everything is right before rendering
  const initializing = !isInitialized.sites || !isInitialized.existedDevices || !isInitialized.inventoryDevices;

  if (initializing) {
    return (
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '150px'
        }}
      >
        <Loading />
      </Box>
    )
  }

  return (
    <>
      <Box ref={scanningAbortRef}>
        { /* Tool bar */}
        {
          hasSearched && (
            <Box className={mainStyle['tools-container']}>
              <Box className={mainStyle['scan-status']}>
                {
                  !scanningDone && (
                    <div className={`${mainStyle['progress']} ${mainStyle['processing']}`}>
                      <img src={icon_search_state} alt="" />
                      Scanning...
                    </div>
                  )
                }
                {
                  scanningDone && !scanningStopped && (
                    <div className={`${mainStyle['progress']} ${mainStyle['done']}`}>
                      <img src={icon_success_state} alt="" />
                      Scan Completed
                    </div>
                  )
                }
                {
                  scanningDone && scanningStopped && (
                    <div className={`${mainStyle['progress']} ${mainStyle['stopped']}`}>
                      <img src={icon_stopped_state} alt="" />
                      Scan Stopped
                    </div>
                  )
                }
              </Box>
              {
                scanningDone ? (
                  <Box
                    onClick={rediscover}
                    className={mainStyle["scan-btn"]}
                  >
                    <img src={icon_start} alt="" />
                    <span>Start</span>
                  </Box>
                ) : (
                  <Box
                    display="flex" alignItems="center"
                    onClick={stopScanningDevices}
                    className={mainStyle["scan-btn"]}
                  >
                    <img src={icon_stop} alt="" />
                    <span>Stop</span>
                  </Box>
                )
              }
              {
                scanningDone ? (
                  <Box
                    onClick={() => setShowModalSettings(true)}
                    className={mainStyle["scan-btn"]}
                  >
                    <img src={icon_setting} alt="" />
                    <span>Discovery Settings</span>
                  </Box>
                ) : (
                  <Box
                    onClick={() => {
                      if (!scanningDone) {
                        return;
                      }
                      setShowModalSettings(true);
                    }}
                    className={`${mainStyle["scan-btn"]} ${mainStyle["scan-btn-disabled"]}`}
                  >
                    <img src={icon_setting_disabled} alt="" />
                    <span>Discovery Settings</span>
                  </Box>
                )
              }
            </Box>
          )
        }
      </Box>

      <Box>
        <TableField
          {...table}
          pendingChildren={
            <>
              {!hasSearched && (
                <>
                  {
                    (auth.scopeLevel === scopeLevelOption.org && sites && sites.length > 0)
                      || (auth.scopeLevel === scopeLevelOption.site) ? (
                      <Box style={{ flex: '1' }}>
                        <Box className={mainStyle["no-data"]}>
                          No Entity in the table
                        </Box>
                        <Box className={mainStyle["start-discovery"]}>
                          <Box
                            className={mainStyle["btn"]}
                            onClick={() => {
                              scanningAbortRef.current = false;
                              setScanningDone(false);
                              setHasSearched(true);
                              cleanDevices();
                              scanDevices();
                            }}>
                            <FormattedMessage className={mainStyle["text"]} id="start-discovery" />
                          </Box>
                        </Box>
                        <Box className={mainStyle["settings"]}>
                          <Box onClick={() => setShowModalSettings(true)}>
                            <FormattedMessage id="settings" />
                          </Box>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Box className={mainStyle["no-data"]}>
                          Please create a site before starting the adoption process.
                        </Box>
                        <Box
                          className={mainStyle["start-discovery"]}
                          onClick={gotoCreateSite}
                        >
                          <Box className={mainStyle["btn"]}>
                            Create Site
                          </Box>
                        </Box>
                        <Box className={mainStyle["settings"]}>
                          <Box onClick={() => setShowModalSettings(true)}>
                            <FormattedMessage id="settings" />
                          </Box>
                        </Box>
                      </>
                    )
                  }
                </>
              )}
              {hasSearched && devices.length === 0 && (
                <Box className={mainStyle["no-data"]} style={{ flex: '1' }}>No data</Box>
              )}
            </>
          }
          currentRowDataIndex={devices.findIndex((device) => device.macAddress === selectedDevice?.macAddress)}
          handleRowOnClick={(row) => {
            const device = devices.find((d) => d.macAddress === row.macAddress);

            if (!device.authStatus) {
              return;
            }

            setSelectedDevice(device);
            setOpenSlider(true);
          }}
        />
      </Box>

      {/* Floating button */}
      {
        !showModalAdoption && (
          <FloatingButton />
        )
      }

      {/* Adoption hint modal */}
      <ModalContainer
        display={showModalAdoptionHint}
        modalWidth={'550px'}
        children={
          <ModalAdoptionHint
            title="Device Adoption"
            content="The adoption command was sent successfully. Return to thee list page and refresh it to see the updated tracking status"
            closeOpenModal={() => { setShowModalAdoptionHint(false); }}
          />
        }
      />

      {/* Settings modal */}
      {
        !initializing && (
          <>
            <ModalContainer
              display={showModalSettings}
              modalWidth={'750px'}
              children={
                <ModalSettings
                  title={< FormattedMessage id="discover-settings" />}
                  closeOpenModal={() => { setShowModalSettings(false); }}
                  proxyDevices={proxyDevices}
                  selectedProxyDevice={selectedProxyDevice}
                  onSelectProxyDevice={handleSelectProxyDevice}
                  layerSetting={layerSetting}
                  setLayerSetting={setLayerSetting}
                  renderCount={renderCount}
                  selectProxyDeviceCallback={selectProxyDeviceCallback}
                  notSelectProxyDeviceCallback={notSelectProxyDeviceCallback}
                />
              }
            />
          </>
        )
      }

      {/* Discovery Error modal */}
      <ModalContainer
        display={showModalDiscoveryError}
        modalWidth={'550px'}
        children={
          <ModalDiscoveryError
            title="Something Went Wrong"
            content="The system is currently busy or discovery is already being performed by another user. Please try again later."
            closeOpenModal={() => { setShowModalDiscoveryError(false); }}
            retryScanningDevices={retryScanningDevices}
          />
        }
      />

      {/* No site error modal */}
      <ModalContainer
        display={showModalNoSiteError}
        modalWidth={'550px'}
        children={
          <ModalError
            title="Device Adoption"
            content="You have not yet created any site. Please create a site before executing adoption."
            closeOpenModal={() => { setShowModalNoSiteError(false); }}
          />
        }
      />

      {/* Adoption modal */}
      <ModalContainer
        display={showModalAdoption}
        modalWidth={showConfirmServiceChangeModal ? '600px' : '90p'}
        children={
          <ModalAdoption
            showModalAdoption={showModalAdoption}
            closeOpenModal={closeAdoption}
            scopeLevel={auth.scopeLevel}
            sites={sites}
            devices={devices}
            setDevices={setDevices}
            selectedDevice={selectedDevice}
            selectedDevices={selectedDevices}
            adoptionSource={adoptionSource}
            sendAdoptionRequest={sendAdoptionRequest}
            readyDevices={readyDevices}
            setReadyDevices={setReadyDevices}
            results={results}
            isNotSupportDNSDevice={isNotSupportDNSDevice}
            showConfirmServiceChangeModal={showConfirmServiceChangeModal}
            setShowConfirmServiceChangeModal={setShowConfirmServiceChangeModal}
            sendPreConfirmRequest={sendPreConfirmRequest}
            preConfirmResult={preConfirmResult}
            isPreConfirmLoading={isPreConfirmLoading}
            setPreConfirmResult={setPreConfirmResult}
          />
        }
      />

      {/* Slider */}
      <Slider
        openSlider={openSlider}
        onToggleSlider={toggleSlider}
        auth={auth}
        device={selectedDevice}
        changeToConfigurable={changeToConfigurable}
        startAdoptProcessForOneDevice={startAdoptProcessForOneDevice}
        isNotSupportDNSDevice={isNotSupportDNSDevice}
      />
    </>
  );
};

export default AddDevice;
